import type { InvalidItemsError } from '@openx/components-core/lib/TransferList/bulk/types';
import { useValidateDomain } from '@openx/components-targeting/Targeting/Domains/hooks/useValidateDomain';
import { normalizeDomain } from '@openx/components-targeting/Targeting/Domains/utils/normalizeDomain';
import { FormTypes, type FormTypesKey } from '@openx/types/inventoryList';
import { useTranslation } from 'react-i18next';

type ValidationResult = {
  errors: InvalidItemsError[];
  validItems: string[];
};

export const useValidate = (type: FormTypesKey) => {
  const { t } = useTranslation();
  const validateDomain = useValidateDomain();

  return (items: string[], itemsFromState: string[]): ValidationResult => {
    const validationFunction = type === FormTypes.DOMAINS ? validateDomain : () => '';
    const normalizationFunction = type === FormTypes.DOMAINS ? normalizeDomain : (key: string) => key;

    const added = new Set(itemsFromState.map(normalizationFunction));

    const duplicates: string[] = [];
    const invalid: string[] = [];
    const validItems: string[] = [];

    for (const rawDomain of items) {
      const normalized = normalizationFunction(rawDomain);
      const errorMsg = validationFunction(rawDomain);

      if (errorMsg) {
        invalid.push(rawDomain);
        continue;
      }

      if (added.has(normalized)) {
        duplicates.push(rawDomain);
      } else {
        validItems.push(normalized);
        added.add(normalized);
      }
    }

    const errors: InvalidItemsError[] = [];

    if (duplicates.length) errors.push({ error: t('Duplicates.'), invalidItems: [...new Set(duplicates)] });

    if (invalid.length) errors.push({ error: t('Invalid Domains.'), invalidItems: [...new Set(invalid)] });

    return { errors, validItems };
  };
};
